import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import useSWR, { useSWRConfig } from "swr";
import {
  get,
  genaiquery,
  swap,
  genairefresh,
  translateTranscript,
  revertTranscript,
  listNotes,
  createNote,
} from "../../api/api";
import { Formatter } from "../../format";
import { TranscriptSegment } from "./TranscriptSegment";
import { Entities } from "./Entities";
import { ValueWithLabel } from "../../components/ValueWithLabel";
import { Placeholder } from "../../components/Placeholder";
import { SentimentChart } from "./SentimentChart";
import { LoudnessChart } from "./LoudnessChart";
import { ComprehendSentimentChart } from "./ComprehendSentimentChart";
import { SpeakerTimeChart } from "./SpeakerTimeChart";
import { ListItems } from "./ListItems";
import { useDangerAlert } from "../../hooks/useAlert";
import "./dashboard.css";
import { getEntityColor } from "./colours";
import { TranscriptOverlay } from "./TranscriptOverlay";
import { range } from "../../util";
import { payloadFromToken } from "../../api/auth";
import { Sentiment } from "../../components/Sentiment";
import { ChatInput } from "../../components/ChatInput";
import {
  Button,
  ContentLayout,
  Spinner,
  Header,
  Grid,
  Container,
  SpaceBetween,
  Select,
  Textarea,
  Input,
} from "@cloudscape-design/components";
import config from "../../config";

const getSentimentTrends = (d, target, labels) => {
  const id = Object.entries(labels).find(([_, v]) => v === target)?.[0];
  if (!id) return {};
  return d?.ConversationAnalytics?.SentimentTrends[id];
};

const createLoudnessData = (segment) => {
  const start = Math.floor(segment.SegmentStartTime);
  const end = Math.floor(segment.SegmentEndTime);
  const r = range(start, end);
  return r.map((item, i) => ({
    x: item,
    y: segment.LoudnessScores[i],
    interruption: segment.SegmentInterruption && item === start ? 100 : null,
    sentiment: (segment.SentimentIsNegative ? -5 : (segment.SentimentIsPositive && segment.LoudnessScores[i] > 0 ? 5 : 0)),
    sentimentScore: segment.SentimentScore,
    silence: (segment.LoudnessScores[i] === 0 ? true : false)
  }));
};

const createSentimentData = (segment) => {
  const start = Math.floor(segment.SegmentStartTime);
  const end = Math.floor(segment.SegmentEndTime);
  const r = range(start, end);
  return r.map((item, i) => ({
    x: item,
    y: (segment.SentimentIsNegative === 1 ? segment.SentimentScore * -1 : segment.SentimentScore)
  }));
}

const buildAudioSrc = (mediaFileUri) => {
  if (!mediaFileUri || !config?.api?.uri) {
    return undefined;
  }

  const url = new URL(`${config.api.uri}/recordings`);
  url.searchParams.set("key", mediaFileUri);

  return url.toString();
};

const getQualityScoreDotClass = (value) => {
  const score = Number(value);
  if (!Number.isFinite(score)) return "status-dot--grey";
  if (score >= 7) return "status-dot--green";
  if (score >= 6) return "status-dot--yellow";
  if (score >= 4) return "status-dot--orange";
  if (score >= 1) return "status-dot--red";
  return "status-dot--grey";
};

function Dashboard({ setAlert }) {
  const { key } = useParams();
  const { mutate } = useSWRConfig();
  const audioElem = useRef();
  const transcriptElem = useRef();

  const { data, error } = useSWR(`/get/${key}`, () => get(key), {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });
  const {
    data: notesData,
    error: notesError,
    mutate: mutateNotes,
  } = useSWR(key ? `/notes/${key}` : null, () => listNotes(key), {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });
  const isTranscribeCallAnalyticsMode =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
      ?.TranscribeApiType === "analytics";

  const hasTranscribeStreamingSession =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
      ?.StreamingSession;

  const usedCustomLanguageModel =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
      ?.CLMName;

  useDangerAlert(error, setAlert);
  useDangerAlert(notesError, setAlert);

  const [speakerLabels, setSpeakerLabels] = useState({});

  const [loudnessData, setLoudnessData] = useState({});

  const [isSwapping, setIsSwapping] = useState(false);

  const [genAiQueries, setGenAiQueries] = useState([]);
  const [genAiQueryStatus, setGenAiQueryStatus] = useState(false);
  const [sourceLang, setSourceLang] = useState({
    label: "Spanish (ES)",
    value: "ES",
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const [isReverting, setIsReverting] = useState(false);
  const [revertError, setRevertError] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteUsername, setNoteUsername] = useState("");
  const [usernameFromToken, setUsernameFromToken] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  const translationLanguageOptions = [
    { label: "Spanish (ES)", value: "ES" },
    { label: "Arabic (AR)", value: "AR" },
  ];

  useEffect(() => {
    const storedToken =
      window.localStorage.getItem("id_token") ||
      window.localStorage.getItem("access_token");

    if (!storedToken) return;

    try {
      const payload = payloadFromToken(storedToken);
      const derivedUsername =
        payload?.["cognito:username"] || payload?.username || payload?.email;
      if (derivedUsername) {
        setNoteUsername(derivedUsername);
        setUsernameFromToken(derivedUsername);
      }
    } catch (err) {
      console.debug("Unable to derive username from token", err);
    }
  }, []);

  useEffect(() => {
    const labels = data?.ConversationAnalytics?.SpeakerLabels || [];
    const newSpeakerLabels = {
      NonTalkTime: "Silence",
      Interruptions: "Interruptions",
      Positive: "Positive",
      Negative: "Negative",
      Neutral: "Neutral"
    };
    labels.forEach(({ Speaker, DisplayText }) => {
      newSpeakerLabels[Speaker] = DisplayText;
    });
    setSpeakerLabels(newSpeakerLabels);

    /*setGenAiQueries(
      (data?.ConversationAnalytics?.GenAiQuery ?
        Object.entries(data?.ConversationAnalytics?.GenAiQuery).map(([key, value]) => {
          return {
            label: key,
            value: (value instanceof Array ? value.join(', ') : value)
          }
        }) : [])
    );*/
  }, [data]);
  
  useEffect(() => {
    const loudness = {};

    if (isTranscribeCallAnalyticsMode) {
      // TCA mode
      let interruptions = [];
      let silence = [];
      let positive = [];
      let negative = [];
      let neutral = [];
      let nonSilence = [];

      Object.keys(speakerLabels).forEach(key => {
        let keyLoudness = (data?.SpeechSegments || [])
        .filter((segment) => segment.SegmentSpeaker === key)
        .map(createLoudnessData)
        .flat();
        
        loudness[key] = keyLoudness;
        let newInterruptions = keyLoudness.filter((d) => d.interruption)
          .map((d) => ({ y: d.interruption, x: d.x }))
        interruptions = interruptions.concat(newInterruptions)

        let newSilence = keyLoudness.filter((d) => d.silence)
          .map((d) => ({ x: d.x, y: 100 }))
        silence = silence.concat(newSilence);

        keyLoudness.forEach((item) => {
          let sentimentItem = {
            x: item.x,
            y: 10,
            sentiment: item.sentiment
          };
          if (item.sentiment > 0) positive.push(sentimentItem)
          else if (item.sentiment < 0) negative.push(sentimentItem)
          else neutral.push(sentimentItem);
          nonSilence[item.x.toString()] = sentimentItem;
        });

      });
      
      // generate the rest of the silence
      if (data) {
        const r = range(0, parseInt(data?.ConversationAnalytics.Duration));
        r.forEach((item, i) => {
          if (!(i in nonSilence)) {
            silence = silence.concat({ x: i, y: 100 });
          }
        });
      }

      loudness['Interruptions'] = interruptions;
      loudness['NonTalkTime'] = silence;
      loudness['Positive'] = positive;
      loudness['Neutral'] = neutral;
      loudness['Negative'] = negative;
    } else {
      // this is transcribe standard
      Object.keys(speakerLabels).forEach(key => {
        if (key.indexOf('spk_') >= 0) {
          let keyLoudness = (data?.SpeechSegments || [])
          .filter((segment) => segment.SegmentSpeaker === key)
          .map(createSentimentData)
          .flat();
          console.log('keyloudness', keyLoudness);
          loudness[key] = keyLoudness;
        }
      });
    }
    console.log('Loudness', loudness);
    setLoudnessData(loudness);
  }, [speakerLabels, data, isTranscribeCallAnalyticsMode])

  /*
  const agentLoudness = (data?.SpeechSegments || [])
    .filter((segment) => segment.SegmentSpeaker === getValueFor("Agent"))
    .map(createLoudnessData)
    .flat();

  const customerLoudness = (data?.SpeechSegments || [])
    .filter((segment) => segment.SegmentSpeaker === getValueFor("Customer"))
    .map(createLoudnessData)
    .flat();*/
  
  const getElementByIdAsync = id => new Promise(resolve => {
    const getElement = () => {
      const element = document.getElementById(id);
      if(element) {
        resolve(element);
      } else {
        requestAnimationFrame(getElement);
      }
    };
    getElement();
  });

  const scrollToBottomOfChat = async () => {
    const chatDiv = await getElementByIdAsync("chatDiv");
    chatDiv.scrollTop = chatDiv.scrollHeight + 200;
  }

  const handleAddNote = async (event) => {
    event?.preventDefault?.();

    if (isSubmittingNote) return;
    if (!noteContent.trim() || !noteUsername.trim()) return;

    setIsSubmittingNote(true);
    try {
      await createNote({
        key,
        content: noteContent.trim(),
        username: noteUsername.trim(),
      });
      setNoteContent("");
      await mutateNotes();
    } catch (err) {
      console.error(err);
      setAlert({
        heading: "Something went wrong",
        variant: "danger",
        text: err?.message || "Unable to add note. Please try again later.",
      });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const submitQuery = (query) => {
    if (genAiQueryStatus === true) {
      return;
    }

    setGenAiQueryStatus(true);
    
    let responseData = {
      label: query,
      value: '...'
    }
    const currentQueries = genAiQueries.concat(responseData);
    setGenAiQueries(currentQueries);
    scrollToBottomOfChat(); 

    let query_response = genaiquery(key, query);
    query_response.then((data) => {
      const queries = currentQueries.map(query => {
        if (query.value !== '...') {
          return query;
        } else {
          return {
            label: query.label,
            value: data.response
          }
        }
      });
      setGenAiQueries(queries);
      scrollToBottomOfChat();
    });
    setGenAiQueryStatus(false);
  }

  const SummaryRefresh = () => {
    const [disabled, setDisabled] = useState(false);

    const onSubmit = async (e) => {
      e.preventDefault();
      setDisabled(true);
      await genairefresh(key);
      setDisabled(false);
      mutate(`/get/${key}`);

      return true;
    }

    return (
        <form onSubmit={onSubmit}>
          <Grid gridDefinition={[{ colspan: { default: 12, xxs: 9 } }, { default: 12, xxs: 3 }]}>
            {disabled ? <Spinner size="big" variant="disabled"/> : <Button disabled={disabled} iconName="refresh" variant="normal" ariaLabel="refresh">
            </Button>}
          </Grid>
        </form>
    );
  };

  const swapAgent = async () => {
    try {
      setIsSwapping(true);
      await swap(key);
      mutate(`/get/${key}`);
    } catch (err) {
      console.error(err);
      setAlert({
        heading: "Something went wrong",
        variant: "danger",
        text: "Unable to swap agent. Please try again later",
      });
    } finally {
      setIsSwapping(false);
    }
  };

  const translateTranscriptToEnglish = async () => {
    if (isTranslating) {
      return;
    }

    setTranslateError(null);
    setRevertError(null);

    try {
      setIsTranslating(true);
      await translateTranscript(key, sourceLang?.value);
      await mutate(`/get/${key}`);
      setAlert({
        heading: "Translation complete",
        variant: "success",
        text: "Transcript translated to English.",
      });
    } catch (err) {
      console.error(err);
      const message = err?.message || "Unable to translate transcript.";
      setTranslateError(message);
      setAlert({
        heading: "Translation failed",
        variant: "danger",
        text: message,
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const revertTranscriptToBackup = async () => {
    if (isReverting || isTranslating) {
      return;
    }

    setTranslateError(null);
    setRevertError(null);

    try {
      setIsReverting(true);
      await revertTranscript(key);
      await mutate(`/get/${key}`);
      setAlert({
        heading: "Revert complete",
        variant: "success",
        text: "Transcript restored from backup.",
      });
    } catch (err) {
      console.error(err);
      const message = err?.message || "Unable to revert transcript.";
      setRevertError(message);
      setAlert({
        heading: "Revert failed",
        variant: "danger",
        text: message,
      });
    } finally {
      setIsReverting(false);
    }
  };

  const setAudioCurrentTime = (e) => {
    const a = document.getElementsByTagName("audio")[0];
    a.currentTime = e.target.dataset.currenttime;
  };

  const callDetailColumn = [
    {
      label: "Timestamp",
      value: (d) =>
        Formatter.TimestampInTimeZone(
          d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions
            ?.callMetadata?.start_time || d?.ConversationAnalytics?.ConversationTime
          ,
          "America/Chicago"
        ),
    },
    { label: "Record Id", value: (d) => d?.ConversationAnalytics?.RecordId },
    {
      label: "Score",
      value: (d) => {
        const score = Number(d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions?.callMetadata?.QualityScore);
        const hasScore = Number.isFinite(score) && score !== 0;
        const dotClass = getQualityScoreDotClass(score);
        return (
          <span className="score-cell">
            <span className={`status-dot ${dotClass}`} />
            <span>{hasScore ? score : "-"}</span>
          </span>
        );
      }
    },
    {
      label: "State",
      value: (d) =>
        d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions
          ?.callMetadata?.Site
    },
    { label: "Agent", value: (d) => d?.ConversationAnalytics?.Agent },
    {
      label: "Agent Name",
      value: (d) =>
        `${d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions?.callMetadata?.FirstName || ""} ${d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions?.callMetadata?.LastName || ""}`.trim()
    },
    {
      label: "Email",
      value: (d) => {
        const email = d?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions?.callMetadata?.Email;
        return email ? <a href={`mailto:${email}`}>{email}</a> : "";
      }
    },
    { label: "Customer", value: (d) => d?.ConversationAnalytics?.Cust },
    {
      label: "Call Duration",
      value: (d) => Formatter.Time(d.ConversationAnalytics.Duration),
    },
    {
      label: "Entity Recognizer Name",
      value: (d) => d?.ConversationAnalytics?.EntityRecognizerName,
    },
    {
      label: "Language Model",
      value: (d) =>
          usedCustomLanguageModel
            ? d?.ConversationAnalytics?.LanguageCode + " [" +
              d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo?.CLMName + "]"
            : d?.ConversationAnalytics?.LanguageCode
    },
    {
      label: "Agent Sentiment",
      value: (d) => (
        <Sentiment
          score={getSentimentTrends(d, "Agent", speakerLabels)?.SentimentScore}
          trend={getSentimentTrends(d, "Agent", speakerLabels)?.SentimentChange}
        />
      ),
    },
    {
      label: "Customer Sentiment",
      value: (d) => (
        <Sentiment
          score={
            getSentimentTrends(d, "Customer", speakerLabels)?.SentimentScore
          }
          trend={
            getSentimentTrends(d, "Customer", speakerLabels)?.SentimentChange
          }
        />
      ),
    },
  ];

  const hiddenCallMetadataLabels = [
    "Entity Recognizer Name",
    "Language Model",
    "Agent Sentiment",
    "Customer Sentiment"
  ];

  const hiddenTranscribeLabels = [
    "Custom Vocabulary",
    "Vocabulary Filter",
    "Average Word Confidence"
  ];

  const transcribeDetailColumn = [
    {
      label: "Type",
      value: (d) =>
        isTranscribeCallAnalyticsMode
          ? hasTranscribeStreamingSession
            ? "Transcribe Streaming Call Analytics"
            : "Transcribe Call Analytics"
          : hasTranscribeStreamingSession
            ? "Transcribe Streaming"
            : "Transcribe"
    },
    {
      label: "Job Id",
      value: (d) => (
        <div key='jobIdKey' className="text-break">
          {
            d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
              ?.TranscriptionJobName
          }
        </div>
      ),
    },
    {
      label: "File Format",
      value: (d) =>
        d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
          ?.MediaFormat,
    },
    {
      label: "Sample Rate",
      value: (d) =>
        d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
          ?.MediaSampleRateHertz,
    },
    {
      label: "PII Redaction",
      value: (d) =>
        d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
          ?.RedactedTranscript === true
          ? "Enabled"
          : "Disabled"
    },
    {
      label: "Custom Vocabulary",
      value: (d) =>
        d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
          ?.VocabularyName,
    },
    {
      label: "Vocabulary Filter",
      value: (d) =>
        d.ConversationAnalytics.SourceInformation[0]?.TranscribeJobInfo
          ?.VocabularyFilter,
    },
    {
      label: "Average Word Confidence",
      value: (d) =>
        Formatter.Percentage(
          d.ConversationAnalytics.SourceInformation[0]?.TranscribeJobInfo
            ?.AverageWordConfidence
        ),
    },
  ];

  let genAiSummary = (data?.ConversationAnalytics?.Summary ?
    Object.entries(data?.ConversationAnalytics?.Summary).map(([key, value]) => {
      let formattedValue;

      if (typeof value === 'string') {
        formattedValue = value;
      } else if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
        formattedValue = value.join(', ');
      } else {
        formattedValue = JSON.stringify(value, null, 2);
      }

      return {
        label: key,
        value: formattedValue
      };
    }) : []);
  
  if (data?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content) {
    // we also have a TCA summary
    genAiSummary = [{
      label: "Contact Summary",
      value: data?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content
    }].concat(genAiSummary)
  };

  const rawAnswers = data?.ConversationAnalytics?.VerbaExtensions?.evaluation?.rawAnswers ||
    data?.ConversationAnalytics?.SourceInformation?.[0]?.VerbaExtensions?.evaluation?.rawAnswers;

  const topicFields = {
    Sales: [
      "professionalGreeting",
      "coverageExplained",
      "crossSellAttempted",
      "customerObjectionsGiven",
      "customerObjectionsDetail",
      "objectionHandlingAttempted",
      "objectionHandlingDetail",
      "policySoldAndPaid",
      "policySaleDetail",
      "autopayOffered",
      "autopayDetail",
    /*  "summary", */
    ],
    "Customer Service": [
      "professionalGreeting",
      "autopayOffered",
      "autopayDetail",
      "issueDetail",
    /*  "summary", */
    ],
  /*  Internal: ["summary"], */
  /*  Personal: ["summary"], */
  };

  const topicFieldLabels = {
    professionalGreeting: "Professional Greeting",
    coverageExplained: "Coverage Explained",
    crossSellAttempted: "Cross-sell Attempted",
    customerObjectionsGiven: "Customer Objections Given",
    customerObjectionsDetail: "Customer Objections Details",
    objectionHandlingAttempted: "Objection Handling Attempted",
    objectionHandlingDetail: "Objection Handling Details",
    policySoldAndPaid: "Policy Sold and Paid",
    policySaleDetail: "Policy Sale Details",
    autopayOffered: "Autopay Offered",
    autopayDetail: "Autopay Details",
    summary: "Summary",
    issueDetail: "Issue Details"
  };

  const topicFromSummary = genAiSummary.find((entry) => entry.label === "Topic")?.value;
  const callType = rawAnswers?.callType || topicFromSummary;
  const allowedFields = callType ? topicFields[callType] : undefined;

  if (rawAnswers && allowedFields) {
    const answerEntries = allowedFields
      .map((field) => {
        const answerValue = rawAnswers[field];
        if (answerValue === null || answerValue === undefined || answerValue === "") {
          return null;
        }
        return {
          label: topicFieldLabels[field] ?? field,
          value: String(answerValue),
        };
      })
      .filter(Boolean);

    if (answerEntries.length > 0) {
      const topicIndex = genAiSummary.findIndex((entry) => entry.label === "Topic");
      if (topicIndex !== -1) {
        genAiSummary = [
          ...genAiSummary.slice(0, topicIndex + 1),
          ...answerEntries,
          ...genAiSummary.slice(topicIndex + 1),
        ];
      }
    }
  }
  
  const audioEndTimestamps = (data?.SpeechSegments || [])
    .map(({WordConfidence}) => WordConfidence)
    .flat()
    .reduce((accumulator, item) => ([...accumulator, item.EndTime]),[]);

  const onAudioPlayTimeUpdate = () => {
    let elementEndTime = undefined;
    for (let i = 0; i < audioEndTimestamps.length; i++) {
      if (audioElem.current.currentTime < audioEndTimestamps[i]) {
        elementEndTime = audioEndTimestamps[i];
        break;
      }
    }

    [...transcriptElem.current.getElementsByClassName('playing')].map(elem => elem.classList?.remove("playing"));
    transcriptElem.current.querySelector('span[data-end="'+elementEndTime+'"]')?.classList?.add("playing");
  };

  const notesList = Array.isArray(notesData?.notes)
    ? notesData.notes
    : Array.isArray(notesData)
    ? notesData
    : [];

  const normalizedNotes = notesList.map((note) => {
    const createdAtDisplay =
      note?.created_at_ct ||
      note?.createdAtCst ||
      note?.created_at ||
      note?.createdAt ||
      note?.created_at_utc ||
      note?.createdAtUtc ||
      "";
    const createdAtSort =
      note?.created_at_utc ||
      note?.createdAtUtc ||
      note?.createdAt ||
      note?.created_at ||
      note?.createdAtCst ||
      note?.created_at_ct ||
      "";

    return {
      ...note,
      username: note?.username || note?.author || note?.user || "",
      content: note?.content || "",
      createdAtDisplay,
      createdAtSort,
    };
  });

  const sortedNotes = [...normalizedNotes].sort((a, b) => {
    const dateA = new Date(a?.createdAtSort || 0);
    const dateB = new Date(b?.createdAtSort || 0);
    return dateB - dateA;
  });

  const notesLoading = !notesData && !notesError;

  

  return (
    <ContentLayout 
    header={
      <Header
          variant="h2"
          actions={[
            <Button key='swapAgent' onClick={swapAgent} disabled={isSwapping} className="float-end">
              {isSwapping ? "Swapping..." : "Swap Agent/Caller"}
            </Button>
          ]}
      >
        Call Details
      </Header>
    }>
    <Grid
        gridDefinition={
          (
            config.genai?.query && isTranscribeCallAnalyticsMode ?
            [
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
            ] : !config.genai?.query && isTranscribeCallAnalyticsMode ?
            [
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
            ] : config.genai?.query && !isTranscribeCallAnalyticsMode ?
            [
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
            ] : [
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 4, m: 4, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 6, m: 6, default: 12 } },
              { colspan: { l: 12, m: 12, default: 12 } },
            ]
          )}
      >

        <Container
          fitHeight={true}
          header={
            <Header variant="h2">
              Call Metadata
            </Header>
          }
        >
          <SpaceBetween size="m">
            {callDetailColumn.map((entry, j) => (
              hiddenCallMetadataLabels.includes(entry.label) ? (
                <div key={j} style={{ display: "none" }}>
                  <ValueWithLabel label={entry.label}>
                    {!data && !error ? (
                      <Placeholder />
                    ) : (
                      entry.value(data) || "-"
                    )}
                  </ValueWithLabel>
                </div>
              ) : (
                <ValueWithLabel key={j} label={entry.label}>
                  {!data && !error ? (
                    <Placeholder />
                  ) : (
                    entry.value(data) || "-"
                  )}
                </ValueWithLabel>
              )
            ))}
          </SpaceBetween>
        </Container>
        <div
          style={{ display: "none" }}
          // fitHeight={true}
          // header={
          //   <Header variant="h2">
          //     Transcribe Details
          //   </Header>
          // }
        >
          <SpaceBetween size="m">
            {transcribeDetailColumn.map((entry, i) => (
              hiddenTranscribeLabels.includes(entry.label) ? (
                <div key={i} style={{ display: "none" }}>
                  <ValueWithLabel label={entry.label}>
                    {!data && !error ? (
                      <Placeholder />
                    ) : (
                      entry.value(data) || "-"
                    )}
                  </ValueWithLabel>
                </div>
              ) : (
                <ValueWithLabel key={i} label={entry.label}>
                  {!data && !error ? (
                    <Placeholder />
                  ) : (
                    entry.value(data) || "-"
                  )}
                </ValueWithLabel>
              )
            ))}
          </SpaceBetween>
        </div>
        <Container
          header={
            <Header variant="h2">
              Sentiment
            </Header>
          }>
            <div style={{ display: "none" }}>
              <SentimentChart
                data={data?.ConversationAnalytics?.SentimentTrends}
                speakerOrder={speakerLabels}
              />
            </div>
            <Header variant="h2">Speaker Time</Header>
            <SpeakerTimeChart
              data={Object.entries(
                data?.ConversationAnalytics?.SpeakerTime || {}
              ).map(([key, value]) => ({
                value: value.TotalTimeSecs,
                label: speakerLabels[key],
                channel: key
              }))}
              speakerOrder={speakerLabels}
            />
        </Container>
        {isTranscribeCallAnalyticsMode && (
          <div style={{ display: "none" }}>
            <Container
              header={
                <Header variant="h2">
                  Loudness/Sentiment
                </Header>
              }
            >
              {!loudnessData && !error ? (
                <div key='noSpeakers'>No Speakers</div>
              ) : (
                <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
              )}
            </Container>
          </div>
        )}
        {!isTranscribeCallAnalyticsMode && (
          <div style={{ display: "none" }}>
            <Container
              header={
                <Header variant="h2">
                  Comprehend Sentiment
                </Header>
              }
            >
              {!loudnessData && !error ? (
                <div key='noSpeakers'>No Speakers</div>
              ) : (
                <ComprehendSentimentChart comprehendSentimentData={loudnessData} speakerLabels={speakerLabels} />
              )}
            </Container>
          </div>
        )}
        <div style={{ display: "none" }}>
          <Container 
            fitHeight={true}
            header={
              <Header variant="h2">
                Entities
              </Header>
            }
          >
            {!data && !error ? (
              <Placeholder />
            ) : (
              <Entities data={data?.ConversationAnalytics?.CustomEntities} />
            )}
          </Container>
        </div>

        {isTranscribeCallAnalyticsMode && (
          <div style={{ display: "none" }}>
            <Container
              fitHeight={true}
              header={
                <Header variant="h2">
                  Categories
                </Header>
            }>
              {!data && !error ? (
                <Placeholder />
              ) : (
                <ListItems
                  data={data?.ConversationAnalytics?.CategoriesDetected.map(
                    (category) => category.Name
                  )}
                />
              )}
            </Container>
          </div>
        )}
        <Container
          fitHeight={true}
          header={
            <Header variant="h2"
              actions = {
                <div style={{ display: "none" }}>
                  <SummaryRefresh />
                </div>
              }
            >
              Generative AI Insights
            </Header>
          }

        >
          <SpaceBetween size="m">
            {genAiSummary.length > 0 ? genAiSummary.map((entry, i) => {
              const hiddenInsightLabels = ["Product", "Resolved", "Callback", "Politeness", "Actions"];
              const isHiddenInsight = hiddenInsightLabels.includes(entry.label);

              if (isHiddenInsight) {
                return (
                  <div key={i} style={{ display: "none" }}>
                    <ValueWithLabel label={entry.label}>
                      { entry.value }
                    </ValueWithLabel>
                  </div>
                );
              }

              return (
                <ValueWithLabel key={i} label={entry.label}>
                  { entry.value }
                </ValueWithLabel>
              );
            }) : <ValueWithLabel key='nosummary'>No Summary Available</ValueWithLabel>}
          </SpaceBetween>
        </Container>

        {config.genai?.query && (
          <Container
            fitHeight={false}
            header={
              <Header variant="h2">
                Generative AI Query
              </Header>
            }
            /* For future use. :) */
            footer={
              <ChatInput submitQuery={submitQuery} />
            }
          >
            <div id="chatDiv" style={{overflow: "hidden", overflowY:'auto', height:'30em'}}>
              <SpaceBetween size="m">
                {genAiQueries.length > 0 ? genAiQueries.map((entry, i) => (
                  <ValueWithLabel key={i} index={i} label={entry.label}>
                      {entry.value === '...' ? <div style={{height:'30px'}}><Spinner/></div> : entry.value}
                    </ValueWithLabel>
                )) : <ValueWithLabel key='nosummary'>Ask a question below.</ValueWithLabel>}
              </SpaceBetween>
            </div>
          </Container>
        )}
        
        {isTranscribeCallAnalyticsMode && (
          <Container
            fitHeight={false}
            header={<Header variant="h2">Notes</Header>}
          >
            <SpaceBetween size="l">
              <div>
                {notesLoading ? (
                  <Placeholder />
                ) : sortedNotes.length > 0 ? (
                  <SpaceBetween size="s">
                    {sortedNotes.map((note, index) => (
                      <div key={note.createdAtSort || note.createdAtDisplay || index}>
                        <div className="text-muted">
                          {[note.createdAtDisplay || "-", note.username || "Unknown"]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        <div>{note.content}</div>
                      </div>
                    ))}
                  </SpaceBetween>
                ) : (
                  <div>No notes yet.</div>
                )}
              </div>
              <form onSubmit={handleAddNote}>
                <SpaceBetween size="s">
                  {usernameFromToken ? (
                    <Input value={noteUsername} disabled />
                  ) : (
                    <Input
                      value={noteUsername}
                      onChange={({ detail }) => setNoteUsername(detail.value)}
                      placeholder="Enter your username"
                    />
                  )}
                  <Textarea
                    rows={3}
                    value={noteContent}
                    onChange={({ detail }) => setNoteContent(detail.value)}
                    placeholder="Add a note about this call"
                  />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={
                      isSubmittingNote ||
                      !noteContent.trim() ||
                      !noteUsername.trim()
                    }
                  >
                    {isSubmittingNote ? "Posting..." : "Add note"}
                  </Button>
                </SpaceBetween>
              </form>
            </SpaceBetween>
          </Container>
        )}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween 
                  direction="horizontal"
                  size="xs"
                >
                  <Select
                    selectedOption={sourceLang}
                    onChange={({ detail }) => setSourceLang(detail.selectedOption)}
                    options={translationLanguageOptions}
                    disabled={isTranslating || isReverting}
                    placeholder="Source language"
                    selectedAriaLabel="Source language"
                    controlId="sourceLangSelector"
                  />
                  <Button
                    key='translateTranscript'
                    onClick={translateTranscriptToEnglish}
                    disabled={isTranslating || isReverting || !sourceLang?.value}
                    iconName="translate"
                    variant="normal"
                    ariaLabel="Translate transcript to English"
                  >
                    {isTranslating ? "Translating..." : "Translate to English"}
                  </Button>
                  <Button
                    key='revertTranscript'
                    onClick={revertTranscriptToBackup}
                    disabled={isTranslating || isReverting}
                    iconName="refresh"
                    variant="normal"
                    ariaLabel="Revert transcript to backup"
                  >
                    {isReverting ? "Reverting..." : "Revert"}
                  </Button>
                  {(isTranslating || isReverting) && <Spinner size="normal" />}
                  {(translateError || revertError) && (
                    <span className="text-danger" style={{ maxWidth: "18rem" }}>
                      {translateError || revertError}
                    </span>
                  )}
                  {data && (
                    <audio
                      key='audoiElem'
                      ref={audioElem}
                      className="float-end"
                      controls
                      src={buildAudioSrc(
                        data?.ConversationAnalytics?.SourceInformation[0]
                          ?.TranscribeJobInfo?.MediaFileUri
                      )}
                      onTimeUpdate={onAudioPlayTimeUpdate}
                    >
                      Your browser does not support the
                      <code>audio</code> element.
                    </audio>
                  )}
                </SpaceBetween>
              }
            >
              Transcript
            </Header>
        }>
          <div ref={transcriptElem}>
          {!data && !error ? (
            <Placeholder />
          ) : (
            (data?.SpeechSegments || []).map((s, i) => (
              <TranscriptSegment
                key={i}
                name={speakerLabels[s.SegmentSpeaker]}
                allSegments={s?.WordConfidence || []}
                segmentStart={s.SegmentStartTime}
                text={s.DisplayText}
                onClick={setAudioCurrentTime}
                highlightLocations={[
                  ...s.EntitiesDetected.map((e) => ({
                    start: e.BeginOffset,
                    end: e.EndOffset,
                    fn: (match, key, start, end, offsetStart, offsetEnd) => (
                      <TranscriptOverlay
                        key={key}
                        colour={getEntityColor(e.Type)}
                        visuallyHidden={`Entity - ${e.Type}`}
                        data-start={start}
                        data-end={end}
                        data-offset-start={offsetStart}
                        data-offset-end={offsetEnd}
                        content={match}
                        type={""}
                        entityOffsetStart={e.BeginOffset}
                        entityOffsetEnd={e.EndOffset}
                        entityClass={"text-danger"}
                        addType={offsetStart === e.BeginOffset ? true : false}
                      >
                      </TranscriptOverlay>
                    ),
                  })),
                  ...(s.IssuesDetected? s.IssuesDetected?.map((issue) => ({
                    start: issue.BeginOffset,
                    end: issue.EndOffset,
                    fn: (match, key, start, end, offsetStart, offsetEnd) => (
                      <TranscriptOverlay
                        key={key}
                        colour="#ffff00"
                        tooltip="Issue"
                        data-start={start}
                        data-end={end}
                        data-offset-start={offsetStart}
                        data-offset-end={offsetEnd}
                        content={match}
                        type={"Issue"}
                        entityOffsetStart={issue.BeginOffset}
                        entityOffsetEnd={issue.EndOffset}
                        entityClass={"text-danger"}
                        addType={offsetStart === issue.BeginOffset ? true : false}
                      >
                      </TranscriptOverlay>
                    ),
                  })) : []),
                  ...(s.ActionItemsDetected? s.ActionItemsDetected?.map((issue) => ({
                    start: issue.BeginOffset,
                    end: issue.EndOffset,
                    fn: (match, key, start, end, offsetStart, offsetEnd) => (
                      <TranscriptOverlay
                        key={key}
                        colour="lightpink"
                        tooltip="Action Item"
                        data-start={start}
                        data-end={end}
                        data-offset-start={offsetStart}
                        data-offset-end={offsetEnd}
                        content={match}
                        type={"Action Item"}
                        entityOffsetStart={issue.BeginOffset}
                        entityOffsetEnd={issue.EndOffset}
                        entityClass={"text-danger"}
                        addType={offsetStart === issue.BeginOffset ? true : false}
                      >
                      </TranscriptOverlay>
                    ),
                  })) : []),
                  ...(s.OutcomesDetected? s.OutcomesDetected?.map((issue) => ({
                    start: issue.BeginOffset,
                    end: issue.EndOffset,
                    fn: (match, key, start, end, offsetStart, offsetEnd) => (
                      <TranscriptOverlay
                        key={key}
                        colour="aquamarine"
                        tooltip="Outcome"
                        data-start={start}
                        data-end={end}
                        data-offset-start={offsetStart}
                        data-offset-end={offsetEnd}
                        content={match}
                        type={"Outcome"}
                        entityOffsetStart={issue.BeginOffset}
                        entityOffsetEnd={issue.EndOffset}
                        entityClass={"text-danger"}
                        addType={offsetStart === issue.BeginOffset ? true : false}
                      >
                      </TranscriptOverlay>
                    ),
                  })) : []),
                ]}
                score={s.SentimentIsPositive - s.SentimentIsNegative}
                interruption={s.SegmentInterruption}
                ivr={s?.IVRSegment || false}
                categoryList={s.CategoriesDetected}
              />
            ))
          )}</div>
        </Container>
      </Grid>
    </ContentLayout>

  );
}

export default Dashboard;
